import { useCallback, useMemo, useState } from 'react'
import { IPalletDetails } from '../../../../modules/API/Pallets/models/palletDetails'
import { Certificates } from '../../../../modules/API/Certificates/certificates'
import { handleNotifications } from '../../../utils/handleNotifications'
import { ITransferItem, QuantityMap } from '../types/transferTeuda.types'

interface UseTransferTeudaParams {
  teudaID: number
  subID: number
  items: IPalletDetails[]
  closeFunc?: () => void
}

interface UseTransferTeudaReturn {
  customerID: number
  quantities: QuantityMap
  isSubmitting: boolean
  selectedCount: number
  handleCustomerChange: (id: number) => void
  handleQtyChange: (recID: number, qty: number) => void
  handleSubmit: () => Promise<void>
}

const useTransferTeuda = ({
  teudaID,
  subID,
  items,
  closeFunc,
}: UseTransferTeudaParams): UseTransferTeudaReturn => {
  const [customerID, setCustomerID] = useState<number>(0)
  const [quantities, setQuantities] = useState<QuantityMap>({})
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleCustomerChange = useCallback((id: number) => {
    setCustomerID(id)
  }, [])

  const handleQtyChange = useCallback((recID: number, qty: number) => {
    setQuantities((prev) => ({ ...prev, [recID]: qty }))
  }, [])

  const selectedItems = useMemo<ITransferItem[]>(
    () =>
      items
        .filter((item) => (quantities[item.RecID] ?? 0) > 0)
        .map((item) => ({
          RecID: item.RecID,
          Quantity: quantities[item.RecID],
        })),
    [items, quantities]
  )

  const selectedCount = selectedItems.length

  const handleSubmit = useCallback(async () => {
    if (customerID === 0) {
      handleNotifications('יש לבחור לקוח יעד', { type: 'warning' })
      return
    }
    if (selectedItems.length === 0) {
      handleNotifications('יש לבחור לפחות פריט אחד עם כמות', { type: 'warning' })
      return
    }

    setIsSubmitting(true)
    try {
      await Certificates.transferTeudaProducts({
        Delivery_Num: teudaID,
        Delivery_Sub: subID,
        CustomerID: customerID,
        Items: selectedItems,
      })
      handleNotifications('הפריטים הועברו בהצלחה', { type: 'success' })
      closeFunc?.()
    } catch {
      handleNotifications('שגיאה בהעברת הפריטים', { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }, [customerID, selectedItems, teudaID, subID, closeFunc])

  return {
    customerID,
    quantities,
    isSubmitting,
    selectedCount,
    handleCustomerChange,
    handleQtyChange,
    handleSubmit,
  }
}

export default useTransferTeuda
